import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { POST, PUT, DELETE } from '../../../configs/api';
import { api } from '../../../utils/constants';
import { getModernFieldSurface, getModernTextFieldSx } from './modernFormFieldStyles';

/** Ensures href is absolute so the browser does not resolve against the current page URL. */
export function formatLinkHref(url) {
  const trimmed = String(url ?? '').trim();
  if (!trimmed) return undefined;
  if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/**
 * Job links UI shared between JobDetailsDialog (persisted) and AddJobDialog (draft).
 * When jobId is omitted, links are managed locally until the parent persists them after job creation.
 */
export default function JobLinksSection({
  jobId,
  links = [],
  onLinksChange,
  user,
  onError,
  isLocked = false,
  isDraft = false,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const fieldSurface = getModernFieldSurface(isDarkMode);
  const textFieldSx = getModernTextFieldSx(isDarkMode);

  const [showAddLinkForm, setShowAddLinkForm] = React.useState(false);
  const [newLinkForm, setNewLinkForm] = React.useState({ label: '', url: '' });
  const [editingLinkId, setEditingLinkId] = React.useState(null);
  const [editingLinkForm, setEditingLinkForm] = React.useState({ label: '', url: '' });
  const [savingJobLink, setSavingJobLink] = React.useState(false);
  const [deletingJobLinks, setDeletingJobLinks] = React.useState({});
  const [jobLinksCollapsed, setJobLinksCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (!jobId && !isDraft) {
      setShowAddLinkForm(false);
      setNewLinkForm({ label: '', url: '' });
      setEditingLinkId(null);
      setEditingLinkForm({ label: '', url: '' });
      setSavingJobLink(false);
      setDeletingJobLinks({});
      setJobLinksCollapsed(false);
    }
  }, [jobId, isDraft]);

  const canEditJobLink = (link) => {
    if (isLocked) return false;
    if (isDraft) return true;
    return String(link?.added_by || '') === String(user?.id || '');
  };

  const validatePayload = (label, url) => {
    const trimmedLabel = label.trim();
    const trimmedUrl = url.trim();
    if (!trimmedLabel || !trimmedUrl) {
      onError?.('Label and URL are required');
      return null;
    }
    return { label: trimmedLabel, url: trimmedUrl };
  };

  const handleCreateJobLinkKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (!savingJobLink) {
        handleCreateJobLink();
      }
    }
  };

  const handleEditJobLinkKeyDown = (event, linkId) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (!savingJobLink) {
        handleUpdateJobLink(linkId);
      }
    }
  };

  const handleCreateJobLink = async () => {
    const payload = validatePayload(newLinkForm.label, newLinkForm.url);
    if (!payload) return;

    if (isDraft) {
      const draftLink = {
        id: crypto.randomUUID(),
        label: payload.label,
        url: payload.url,
        added_by: user?.id,
      };
      onLinksChange?.([draftLink, ...(links || [])]);
      setNewLinkForm({ label: '', url: '' });
      setShowAddLinkForm(false);
      return;
    }

    if (!jobId) return;

    setSavingJobLink(true);
    try {
      const { data, status, error, message } = await POST(`${api.createJobLink}/${jobId}/links`, {}, payload);
      if (!error && status === 201) {
        onLinksChange?.([data, ...(links || [])]);
        setNewLinkForm({ label: '', url: '' });
        setShowAddLinkForm(false);
      } else {
        onError?.(message || 'Failed to add link');
      }
    } catch (err) {
      onError?.(err?.response?.data?.message || 'Failed to add link');
    } finally {
      setSavingJobLink(false);
    }
  };

  const startEditJobLink = (link) => {
    setEditingLinkId(link.id);
    setEditingLinkForm({ label: link.label || '', url: link.url || '' });
  };

  const cancelEditJobLink = () => {
    setEditingLinkId(null);
    setEditingLinkForm({ label: '', url: '' });
  };

  const handleUpdateJobLink = async (linkId) => {
    const payload = validatePayload(editingLinkForm.label, editingLinkForm.url);
    if (!payload) return;

    if (isDraft) {
      onLinksChange?.(
        (links || []).map((link) =>
          link.id === linkId ? { ...link, label: payload.label, url: payload.url } : link
        )
      );
      cancelEditJobLink();
      return;
    }

    if (!jobId) return;

    setSavingJobLink(true);
    try {
      const { data, status, error, message } = await PUT(
        `${api.updateJobLink}/${jobId}/links/${linkId}`,
        {},
        payload
      );
      if (!error && status === 200) {
        onLinksChange?.((links || []).map((link) => (link.id === linkId ? data : link)));
        cancelEditJobLink();
      } else {
        onError?.(message || 'Failed to update link');
      }
    } catch (err) {
      onError?.(err?.response?.data?.message || 'Failed to update link');
    } finally {
      setSavingJobLink(false);
    }
  };

  const handleDeleteJobLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;

    if (isDraft) {
      onLinksChange?.((links || []).filter((link) => link.id !== linkId));
      return;
    }

    if (!jobId) return;

    setDeletingJobLinks((prev) => ({ ...prev, [linkId]: true }));
    try {
      const { status, error, message } = await DELETE(`${api.deleteJobLink}/${jobId}/links/${linkId}`);
      if (!error && status === 200) {
        onLinksChange?.((links || []).filter((link) => link.id !== linkId));
      } else {
        onError?.(message || 'Failed to delete link');
      }
    } catch (err) {
      onError?.(err?.response?.data?.message || 'Failed to delete link');
    } finally {
      setDeletingJobLinks((prev) => {
        const next = { ...prev };
        delete next[linkId];
        return next;
      });
    }
  };

  return (
    <Box sx={{ border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', borderRadius: 2, p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setJobLinksCollapsed((prev) => !prev)}
            sx={{
              transform: jobLinksCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon fontSize="small" />
            Job Links ({links.length})
          </Typography>
        </Box>
        {!jobLinksCollapsed && !isLocked && (
          <IconButton
            size="small"
            onClick={() => {
              setShowAddLinkForm((prev) => !prev);
              if (editingLinkId) {
                cancelEditJobLink();
              }
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!jobLinksCollapsed && showAddLinkForm && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <TextField
            size="small"
            label="Label"
            value={newLinkForm.label}
            onKeyDown={handleCreateJobLinkKeyDown}
            onChange={(e) => setNewLinkForm((prev) => ({ ...prev, label: e.target.value }))}
            sx={{ minWidth: 170, flex: 1, ...textFieldSx }}
            slotProps={{ input: { maxLength: 120 } }}
          />
          <TextField
            size="small"
            label="URL"
            value={newLinkForm.url}
            onKeyDown={handleCreateJobLinkKeyDown}
            onChange={(e) => setNewLinkForm((prev) => ({ ...prev, url: e.target.value }))}
            sx={{ minWidth: 240, flex: 2, ...textFieldSx }}
          />
          <IconButton size="small" onClick={handleCreateJobLink} disabled={savingJobLink}>
            {savingJobLink ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" color="success" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setShowAddLinkForm(false);
              setNewLinkForm({ label: '', url: '' });
            }}
          >
            <CancelIcon fontSize="small" color="error" />
          </IconButton>
        </Box>
      )}

      {!jobLinksCollapsed &&
        (links.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No links added for this job yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {links.map((link) => (
              <Box
                key={link.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.75,
                  borderRadius: 1,
                  border: isDarkMode ? '1px solid #334155' : '1px solid #ececec',
                  backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.45)' : '#fafafa',
                }}
              >
                {editingLinkId === link.id ? (
                  <>
                    <TextField
                      size="small"
                      label="Label"
                      value={editingLinkForm.label}
                      onKeyDown={(e) => handleEditJobLinkKeyDown(e, link.id)}
                      onChange={(e) => setEditingLinkForm((prev) => ({ ...prev, label: e.target.value }))}
                      sx={{ minWidth: 170, flex: 1, ...textFieldSx }}
                      slotProps={{ input: { maxLength: 120 } }}
                    />
                    <TextField
                      size="small"
                      label="URL"
                      value={editingLinkForm.url}
                      onKeyDown={(e) => handleEditJobLinkKeyDown(e, link.id)}
                      onChange={(e) => setEditingLinkForm((prev) => ({ ...prev, url: e.target.value }))}
                      sx={{ minWidth: 240, flex: 2, ...textFieldSx }}
                    />
                    <IconButton size="small" onClick={() => handleUpdateJobLink(link.id)} disabled={savingJobLink}>
                      {savingJobLink ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" color="success" />}
                    </IconButton>
                    <IconButton size="small" onClick={cancelEditJobLink}>
                      <CancelIcon fontSize="small" color="error" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.2 }}>
                        {link.label}
                      </Typography>
                      <Typography
                        component="a"
                        href={formatLinkHref(link.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.4,
                          color: fieldSurface.accent,
                          fontSize: '0.74rem',
                          fontWeight: 500,
                          wordBreak: 'break-all',
                          mt: 0.1,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {link.url}
                        <OpenInNewIcon sx={{ fontSize: 12 }} />
                      </Typography>
                    </Box>
                    {canEditJobLink(link) && (
                      <>
                        <IconButton size="small" onClick={() => startEditJobLink(link)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteJobLink(link.id)}
                          disabled={!!deletingJobLinks[link.id]}
                        >
                          {deletingJobLinks[link.id] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" color="error" />
                          )}
                        </IconButton>
                      </>
                    )}
                  </>
                )}
              </Box>
            ))}
          </Box>
        ))}
    </Box>
  );
}

/** Persist draft links after job creation. Returns failed count. */
export async function saveJobLinksForJob(jobId, links) {
  if (!jobId || !links?.length) return 0;

  let failed = 0;
  for (const link of links) {
    const payload = {
      label: String(link.label || '').trim(),
      url: String(link.url || '').trim(),
    };
    if (!payload.label || !payload.url) {
      failed += 1;
      continue;
    }
    try {
      const { status, error } = await POST(`${api.createJobLink}/${jobId}/links`, {}, payload);
      if (error || (status !== 200 && status !== 201)) {
        failed += 1;
      }
    } catch {
      failed += 1;
    }
  }
  return failed;
}
